import React, { useMemo } from 'react';
import SVGColorBlock from '../SVGColorBlock';
import { JULES_EVENT_CLASS_LABELS } from './entitlement';

const SCOPE_ORDER = ['local', 'provincial', 'national', 'global'];
const SCOPE_LABELS = {
	local: 'Local',
	provincial: 'Provincial / state',
	national: 'National',
	global: 'Global',
};

/**
 * Preview-only Jules suggestions. Actions are explicit buttons only.
 */
export default function AiEventSuggestions({
	loading,
	error,
	events,
	context = 'scene',
	onUseName,
	onApplyColors,
	onCreateSceneAndLink,
	onApplyColorsToLinkedScene,
	hasLinkedScene = false,
}) {
	const groups = useMemo(() => {
		if (!events) return [];
		return SCOPE_ORDER
			.map((scope) => ({
				scope,
				label: SCOPE_LABELS[scope],
				items: Array.isArray(events[scope]) ? events[scope] : [],
			}))
			.filter((g) => g.items.length > 0);
	}, [events]);

	if (!loading && !error && groups.length === 0) {
		return null;
	}

	return (
		<div className="rounded-xl border border-[#880e4f]/30 bg-[#880e4f]/5 dark:bg-[#880e4f]/10 p-3 space-y-3">
			<div className="flex items-center justify-between gap-2">
				<div className="text-sm font-medium text-[#880e4f]">Jules suggestions</div>
				{loading && (
					<span className="text-[10px] text-gray-500 dark:text-gray-400">Searching…</span>
				)}
			</div>

			{error && (
				<p className="text-xs text-red-600 dark:text-red-400">{error}</p>
			)}

			{groups.map((group) => (
				<div key={group.scope} className="space-y-2">
					<div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
						{group.label}
					</div>
					{group.items.map((event, idx) => (
						<div
							key={`${group.scope}-${event.name}-${idx}`}
							className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-2.5 space-y-2"
						>
							<div className="flex items-start gap-2">
								<div className="flex-1 min-w-0">
									<div className="text-sm font-medium truncate">{event.name}</div>
									{event.date && (
										<div className="text-[10px] text-gray-500 dark:text-gray-400">{event.date}</div>
									)}
									{event.summary && (
										<p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5 line-clamp-2">
											{event.summary}
										</p>
									)}
									<div className="text-[10px] text-gray-400 mt-1">
										{JULES_EVENT_CLASS_LABELS[event.eventClass] || event.eventClass}
										{event.colorSource === 'expert' && event.expertMethodId
											? ` · expert: ${event.expertMethodId}`
											: ' · AI colors'}
									</div>
								</div>
								{event.colors?.length > 0 && (
									<SVGColorBlock size={36} data={event.colors} />
								)}
							</div>

							<div className="flex flex-wrap gap-1.5">
								{onUseName && (
									<button
										type="button"
										onClick={() => onUseName(event)}
										className="text-[11px] px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800"
									>
										Use name
									</button>
								)}
								{onApplyColors && event.colors?.length > 0 && (
									<button
										type="button"
										onClick={() => onApplyColors(event)}
										className="text-[11px] px-2 py-1 rounded-lg border border-[#880e4f]/40 text-[#880e4f] hover:bg-[#880e4f]/10"
									>
										Apply colors
									</button>
								)}
								{context === 'schedule' && onCreateSceneAndLink && (
									<button
										type="button"
										onClick={() => onCreateSceneAndLink(event)}
										className="text-[11px] px-2 py-1 rounded-lg bg-[#880e4f] text-white hover:bg-[#6b0b3f]"
									>
										Create scene &amp; link
									</button>
								)}
								{context === 'schedule' && hasLinkedScene && onApplyColorsToLinkedScene && event.colors?.length > 0 && (
									<button
										type="button"
										onClick={() => onApplyColorsToLinkedScene(event)}
										className="text-[11px] px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800"
									>
										Apply colors to linked scene
									</button>
								)}
							</div>
						</div>
					))}
				</div>
			))}
		</div>
	);
}
